import { useEffect } from 'react'

/** url → in-flight / completed load promise */
const scriptLoads = new Map()

function loadScriptOnce(url, attributes = {}) {
  if (scriptLoads.has(url)) return scriptLoads.get(url)

  const existing = [...document.querySelectorAll('script')].find(
    (node) => node.getAttribute('src') === url || node.src === url,
  )
  if (existing) {
    const promise =
      existing.dataset.hfLoaded === 'true'
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener('load', () => {
              existing.dataset.hfLoaded = 'true'
              resolve()
            })
            existing.addEventListener('error', reject)
          })
    scriptLoads.set(url, promise)
    return promise
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    Object.entries(attributes).forEach(([key, value]) => {
      if (value != null) script.setAttribute(key, value)
    })
    script.onload = () => {
      script.dataset.hfLoaded = 'true'
      resolve()
    }
    script.onerror = (event) => reject(event)
    document.head.appendChild(script)
  })

  scriptLoads.set(url, promise)
  return promise
}

/**
 * Drop-in for `react-load-script`.
 * Renders nothing; injects `<script src={url}>` once and calls onLoad/onError.
 */
export default function Script({
  url,
  onLoad,
  onError,
  attributes,
}) {
  useEffect(() => {
    if (!url) return undefined
    let cancelled = false

    loadScriptOnce(url, attributes)
      .then(() => {
        if (!cancelled) onLoad?.()
      })
      .catch((error) => {
        if (!cancelled) onError?.(error)
      })

    return () => {
      cancelled = true
    }
    // Match react-load-script: reload only when url changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  return null
}

export { loadScriptOnce }
